import type { SafetyWorkflowStepId } from '../safetyWorkflowStepMapping';
import type { GroupedWorkflowStepperVisualStatus } from './groupedSafetyCaseStepLabels';
import { GROUPED_SAFETY_CASE_STEP_LABELS } from './groupedSafetyCaseStepLabels';
import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';

/** User-facing label; internal gating still uses `blocked` booleans. */
export const GROUPED_REPORT_READINESS_PENDING_LABEL = 'Pending required steps';

export const GROUPED_NEXT_REQUIRED_ACTION_TITLE = 'Next required action';

export const GROUPED_REPRESENTATIVE_EVENT_HELPER =
  'This evidence sample is used to review the recurring risk pattern and decide whether it requires ongoing management.';

export function groupedCurrentWorkflowStageLabel(stepId: SafetyWorkflowStepId): string {
  switch (stepId) {
    case 'detected':
      return GROUPED_SAFETY_CASE_STEP_LABELS.riskPattern;
    case 'review':
      return GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview;
    case 'assign_action':
      return GROUPED_SAFETY_CASE_STEP_LABELS.correctiveAction;
    case 'track_impact':
      return GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking;
    case 'report':
      return GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport;
    case 'management_decision':
      return 'Does this require management?';
    case 'managed_in_case':
      return 'Managed in Safety Case';
    default:
      return GROUPED_SAFETY_CASE_STEP_LABELS.riskPattern;
  }
}

export function groupedPersistentWorkflowStateLabel(args: {
  followUpStarted?: boolean;
  correctiveActionComplete?: boolean;
  followUpMeasured?: boolean;
  reportReady?: boolean;
  currentStepId: SafetyWorkflowStepId;
}): string {
  if (args.reportReady || args.currentStepId === 'report') {
    return GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport;
  }
  if (args.followUpStarted && !args.correctiveActionComplete) {
    return 'Monitoring';
  }
  if (args.currentStepId === 'track_impact' && !args.followUpMeasured) {
    return 'Impact measurement';
  }
  return groupedCurrentWorkflowStageLabel(args.currentStepId);
}

export function buildGroupedNextRequiredActionCopy(args: {
  nextStepLabel: string;
  reportBlocked: boolean;
}): {
  title: string;
  body: string;
  secondaryText: string;
} {
  return {
    title: GROUPED_NEXT_REQUIRED_ACTION_TITLE,
    body: args.nextStepLabel,
    secondaryText: args.reportBlocked
      ? 'Report preparation stays pending until evidence review, corrective action completion, and impact measurement are satisfied.'
      : 'Complete the remaining workflow steps before preparing the Safety Report draft.',
  };
}

export function buildGroupedBlockerSummary(
  args:
    | string[]
    | {
      signOffMissingItems: string[];
      trackingInProgress?: boolean;
    },
): string {
  const signOffMissingItems = Array.isArray(args) ? args : args.signOffMissingItems;
  const trackingInProgress = Array.isArray(args) ? false : args.trackingInProgress;
  if (signOffMissingItems.length === 0) return '';
  const joined = signOffMissingItems.join(' ').toLowerCase();
  const needsCorrective =
    joined.includes('corrective action')
    || joined.includes('complete corrective action')
    || joined.includes('action owner')
    || joined.includes('risk owner')
    || joined.includes('evidence review')
    || joined.includes('confirm safety');
  const needsImpact = joined.includes('impact');
  if (needsCorrective && needsImpact) {
    return 'Corrective action and impact measurement are incomplete.';
  }
  if (needsImpact) {
    if (trackingInProgress) {
      return 'Action applied and tracking is in progress. No impact result is available yet.';
    }
    return 'Impact measurement is incomplete.';
  }
  if (needsCorrective) {
    return 'Corrective action has not been completed.';
  }
  return `Outstanding: ${signOffMissingItems.join(', ')}.`;
}

export function groupedWorkflowStepperStatusHint(
  stepId: SafetyWorkflowStepId,
  status: GroupedWorkflowStepperVisualStatus,
): string | null {
  if (stepId === 'track_impact' && status === 'locked') {
    return 'Locked until corrective follow-up has started.';
  }
  if (stepId === 'report' && status === 'blocked') {
    return 'Pending corrective action and impact measurement.';
  }
  if (stepId === 'assign_action' && status === 'locked') {
    return 'Complete evidence review before corrective action.';
  }
  return null;
}

export function groupedVerifyEvidencePanelDescription(
  status: 'done' | 'current' | 'pending' | 'locked' | 'blocked',
  evidenceConfirmed?: boolean,
): string {
  if (status === 'done' && evidenceConfirmed) {
    return 'Representative evidence confirms a recurring risk pattern that requires ongoing management.';
  }
  return 'Confirm whether the representative evidence shows a recurring risk pattern that requires ongoing management.';
}

export function groupedCorrectiveActionPanelDescriptionUx(
  correctiveActionLocked?: boolean,
  status?: 'done' | 'current' | 'pending' | 'locked' | 'blocked',
): string {
  if (correctiveActionLocked) {
    return 'Complete evidence review before choosing a corrective action.';
  }
  if (status === 'done') {
    return 'Corrective follow-up is underway. Update the plan, track execution, or close the representative review when ready.';
  }
  if (status === 'current') {
    return 'Assign an owner and define the corrective action for this recurring risk pattern.';
  }
  return 'Choose the next corrective action for this recurring risk pattern.';
}

export function groupedImpactTrackingPanelDescriptionUx(impactTrackingLocked?: boolean): string {
  if (impactTrackingLocked) {
    return 'Locked until corrective follow-up has started.';
  }
  return 'Record the follow-up observation for this recurring risk pattern in the selected review window. This is not multi-day trend proof from a short clip.';
}

export function groupedSafetyReportPanelDescriptionUx(reportBlocked?: boolean): string {
  if (reportBlocked) {
    return 'Final sign-off requires evidence review, corrective action completion or validation, and measured or explicitly waived impact measurement.';
  }
  return 'Prepare the Safety Report draft once required workflow steps are complete.';
}

export function formatGroupedReportReadinessChipLabel(readinessLabel: string | undefined): string {
  const label = String(readinessLabel || '').trim();
  if (!label || /^blocked$/i.test(label)) {
    return GROUPED_REPORT_READINESS_PENDING_LABEL;
  }
  return label;
}

export function buildGroupedPersistentNextStepLabelUx(args: {
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  followUpStarted?: boolean;
  correctiveActionComplete?: boolean;
  followUpMeasured?: boolean;
  reportReady?: boolean;
}): string {
  if (!args.representativeReviewComplete) {
    return 'Review representative evidence and confirm whether this recurring risk pattern requires corrective action.';
  }
  if (!args.evidenceConfirmed) {
    return 'Confirm the risk pattern in Evidence review before starting corrective action.';
  }
  if (ownerRoleIsUnassigned(args.patternOwnerLabel)) {
    return 'Assign a risk owner in Risk pattern, then continue to Corrective action.';
  }
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) {
    return 'Assign an action owner and define the corrective action.';
  }
  if (!args.followUpStarted) {
    return 'Start corrective follow-up or link the work order before moving on to impact measurement.';
  }
  if (!args.followUpMeasured) {
    return 'The workflow is now in Monitoring. Review impact measurement while corrective follow-up continues.';
  }
  if (args.reportReady) {
    return 'Open Report preparation and review the Safety Report draft plus supporting proof package.';
  }
  return 'Open Report preparation for this recurring risk pattern.';
}
